import {
  FETCH_BOOKINGS,
  FETCH_BOOKINGS_SUCCESS,
  FETCH_BOOKINGS_FAILED,
  UPDATE_BOOKING,
  CANCEL_BOOKING
} from "../store/types";
import { fetchBookingLocations } from "../actions/locationactions";
import { getEffectiveUserRole } from "../actions/authactions";
import { RequestPushMsg } from "../other/NotificationFunctions";
import store from "../store/store";
import { firebase } from "../config/configureFirebase";
import { addActualsToBooking, saveAddresses, updateDriverQueue } from "../other/sharedFunctions";
import { get, onValue, update, off, remove, push, query, orderByChild, equalTo, ref } from "firebase/database";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { normalizeBookingPayload } from "../booking/utils/normalizeBookingPayload";
import { stripUndefinedDeep } from "../booking/utils/stripUndefinedDeep";
import { isDriverRideInProgress } from "../other/authHelpers";

/**
 * [STATE_MACHINE]
 * booking.status (STRICT): NEW | ACCEPTED | COMPLETED | CANCELLED
 * booking.trip_status (tracking): ARRIVED | STARTED | REACHED (optional)
 * booking.payment.status (STRICT): PENDING | PAID | REFUNDED
 * legacy mirrors kept: payment_status, payment_mode, transaction_id
 */

const norm = (s) => String(s || "").toUpperCase().trim();

const ensurePaymentContainer = (booking) => {
  if (!booking.payment) booking.payment = {};
  if (!booking.payment.status && booking.payment_status) booking.payment.status = booking.payment_status;
  if (!booking.payment.transaction_id && booking.transaction_id) booking.payment.transaction_id = booking.transaction_id;

  // If legacy payment_mode exists, keep provider best-effort (never set onboard as provider)
  const pm = String(booking.payment_mode || "").toLowerCase();
  if (!booking.payment.provider && pm && pm !== "onboard") booking.payment.provider = pm;

  return booking;
};

const syncLegacyPaymentMirrors = (booking) => {
  booking = ensurePaymentContainer(booking);

  // status strict
  const payStatus = norm(booking.payment.status || booking.payment_status || "PENDING");
  booking.payment.status = ["PENDING", "PAID", "REFUNDED"].includes(payStatus) ? payStatus : "PENDING";
  booking.payment_status = booking.payment.status;

  // Preserve custom admin field
  if (booking.payment.amountPaid !== undefined) {
    booking.payment.amountPaid = Number(booking.payment.amountPaid);
  }

  // provider: null means onboard
  const provider = booking.payment.provider === null ? null : (booking.payment.provider || null);
  booking.payment.provider = provider;
  booking.payment_mode = provider ? provider : "onboard";

  // tx mirror
  if (booking.payment.transaction_id) booking.transaction_id = booking.payment.transaction_id;

  // minimal canonical payment.mode if missing
  if (!booking.payment.mode) booking.payment.mode = provider ? "ONLINE" : "ONBOARD";

  // timestamp (optional)
  if (!booking.payment.updatedAt) booking.payment.updatedAt = new Date().toISOString();

  return booking;
};

const sanitizeUndefined = (obj) => {
  const out = {};
  Object.keys(obj || {}).forEach((k) => {
    if (obj[k] !== undefined) out[k] = obj[k];
  });
  return out;
};

export const getBookingDateMs = (item) => {
  if (!item) return 0;
  const raw = item.tripdate || item.bookingDate || item.booking_date || item.scheduledAt || item.startTime || item.createdAt || item.created_at || item.endTime;
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return isNaN(time) ? 0 : time;
};

/**
 * Décompose les réservations avec trajet aller-retour en 2 prestations distinctes (Aller et Retour).
 * Aligné avec l'affichage de l'espace chauffeur web-app (DriverOverview.js).
 */
export const expandRoundTripBookings = (bookings) => {
  if (!Array.isArray(bookings)) return [];
  const result = [];
  for (const b of bookings) {
    // Si la réservation est DÉJÀ scindée (système natif par legs séparés dans Firebase), ne JAMAIS la re-découper
    const isAlreadySplit = Boolean(
      b.bundleId ||
      b.linkedBookingId ||
      (typeof b.id === "string" && (b.id.endsWith("_outbound") || b.id.endsWith("_return"))) ||
      (typeof b.reference === "string" && (b.reference.endsWith("-O") || b.reference.endsWith("-R"))) ||
      ((b.leg === "return" || b.isReturn === true) && !b.returnTrip?.enabled)
    );

    if (isAlreadySplit) {
      result.push(b);
      continue;
    }

    const hasReturn = !!(
      b.returnTrip?.enabled === true ||
      b.returnTrip?.tripdate ||
      b.returnTrip?.date
    );

    if (!hasReturn) {
      result.push(b);
      continue;
    }

    const bookingTotal = Number(b.trip_cost ?? b.estimate ?? 0);
    const rawBase = Number(b.estimate?.breakdown?.baseFare || b.estimate?.breakdown?.outbound?.total || 0);
    const rawReturn = Number(b.estimate?.breakdown?.returnFare || b.estimate?.breakdown?.return?.total || 0);
    let ratioOutbound = 0.5;
    if (rawBase > 0 && rawReturn > 0) {
      ratioOutbound = rawBase / (rawBase + rawReturn);
    }
    const legPriceOutbound = Math.round(bookingTotal * ratioOutbound * 100) / 100;
    const legPriceReturn = Math.round((bookingTotal - legPriceOutbound) * 100) / 100;

    const commRate = parseFloat(b.fleet_admin_comission || 0);
    const feeType = b.fleet_admin_fee_type || 'percentage';
    const driverShareTotal = (b.driver_share !== undefined && b.driver_share !== null) ? Number(b.driver_share) : null;
    let legShareOutbound = null;
    let legShareReturn = null;

    if (feeType !== 'flat' && commRate > 0) {
      legShareOutbound = Math.round((legPriceOutbound / (1 + commRate / 100)) * 100) / 100;
      legShareReturn = Math.round((legPriceReturn / (1 + commRate / 100)) * 100) / 100;
    } else if (driverShareTotal !== null) {
      legShareOutbound = Math.round(driverShareTotal * ratioOutbound * 100) / 100;
      legShareReturn = Math.round((driverShareTotal - legShareOutbound) * 100) / 100;
    }

    // Date retour résolue avant la création des items Aller et Retour
    const returnTripDateStr = b.returnTrip?.tripdate || b.returnTrip?.date || b.returnTrip?.scheduledAt || b.returnDate;

    // Référence pour les legs dédoublés
    const baseRef = b.reference || b.instructionData?.referenceCode || "";
    const outboundRef = baseRef && !baseRef.endsWith("-O") && !baseRef.endsWith("-R") ? `${baseRef}-O` : baseRef;
    const returnRef = baseRef && !baseRef.endsWith("-O") && !baseRef.endsWith("-R") ? `${baseRef}-R` : baseRef;

    // 1. Trajet Aller (Outbound)
    const outboundPickupAddr = b.pickup?.address || b.pickup?.add || b.pickupAddress || "";
    const outboundPickupLat = b.pickup?.lat ?? b.pickup?.coords?.lat ?? (b.coords && b.coords[0] ? (b.coords[0].latitude ?? b.coords[0].lat) : null);
    const outboundPickupLng = b.pickup?.lng ?? b.pickup?.coords?.lng ?? (b.coords && b.coords[0] ? (b.coords[0].longitude ?? b.coords[0].lng) : null);
    const outboundPickup = {
      ...(b.pickup || {}),
      address: outboundPickupAddr,
      add: outboundPickupAddr,
      description: outboundPickupAddr,
      lat: outboundPickupLat,
      lng: outboundPickupLng,
      coords: { lat: outboundPickupLat, lng: outboundPickupLng }
    };

    const outboundDropAddr = b.drop?.address || b.drop?.add || b.dropAddress || "";
    const outboundDropLat = b.drop?.lat ?? b.drop?.coords?.lat ?? (b.coords && b.coords[1] ? (b.coords[1].latitude ?? b.coords[1].lat) : null);
    const outboundDropLng = b.drop?.lng ?? b.drop?.coords?.lng ?? (b.coords && b.coords[1] ? (b.coords[1].longitude ?? b.coords[1].lng) : null);
    const outboundDrop = {
      ...(b.drop || {}),
      address: outboundDropAddr,
      add: outboundDropAddr,
      description: outboundDropAddr,
      lat: outboundDropLat,
      lng: outboundDropLng,
      coords: { lat: outboundDropLat, lng: outboundDropLng }
    };

    const outboundItem = {
      ...b,
      id: `${b.id}_outbound`,
      bookingId: b.id,
      linkedBookingId: b.linkedBookingId || `${b.id}_return`,
      reference: outboundRef || b.reference,
      tripdate: b.tripdate,
      bookingDate: b.tripdate,
      pickupAddress: outboundPickupAddr,
      dropAddress: outboundDropAddr,
      pickup: outboundPickup,
      drop: outboundDrop,
      leg: "outbound",
      isReturn: false,
      hasReturn: true,
      legLabel: "ALLER",
      trip_cost: legPriceOutbound,
      driver_share: legShareOutbound !== null ? legShareOutbound : b.driver_share,
      roundTripTotal: bookingTotal,
      returnTripDate: returnTripDateStr,
      pickupFlightNumber: b.instructionData?.pickupFlightNumber || b.pickupFlightNumber || b.flightNumber || null,
      pickupTrainNumber: b.instructionData?.pickupTrainNumber || b.pickupTrainNumber || b.trainNumber || null,
      flightNumber: b.instructionData?.pickupFlightNumber || b.pickupFlightNumber || b.flightNumber || null,
      trainNumber: b.instructionData?.pickupTrainNumber || b.pickupTrainNumber || b.trainNumber || null,
      vehicleNumber: b.vehicleNumber || b.vehicle_number || null,
      vehicle_number: b.vehicle_number || b.vehicleNumber || null,
      vehicleMake: b.vehicleMake || null,
      vehicleModel: b.vehicleModel || null,
      vehicleColor: b.vehicleColor || null,
      vehicleYear: b.vehicleYear || null,
    };
    result.push(outboundItem);

    // 2. Trajet Retour (Return)
    const returnPickupAddr = b.returnTrip?.pickup?.address || b.returnTrip?.pickup?.add || b.returnTrip?.pickupAddress || outboundDropAddr;
    const returnPickupLat = b.returnTrip?.pickup?.lat ?? b.returnTrip?.pickup?.coords?.lat ?? outboundDropLat;
    const returnPickupLng = b.returnTrip?.pickup?.lng ?? b.returnTrip?.pickup?.coords?.lng ?? outboundDropLng;

    const returnPickup = {
      ...(b.returnTrip?.pickup || {}),
      address: returnPickupAddr,
      add: returnPickupAddr,
      description: returnPickupAddr,
      lat: returnPickupLat,
      lng: returnPickupLng,
      coords: { lat: returnPickupLat, lng: returnPickupLng }
    };

    const returnDropAddr = b.returnTrip?.drop?.address || b.returnTrip?.drop?.add || b.returnTrip?.dropAddress || outboundPickupAddr;
    const returnDropLat = b.returnTrip?.drop?.lat ?? b.returnTrip?.drop?.coords?.lat ?? outboundPickupLat;
    const returnDropLng = b.returnTrip?.drop?.lng ?? b.returnTrip?.drop?.coords?.lng ?? outboundPickupLng;

    const returnDrop = {
      ...(b.returnTrip?.drop || {}),
      address: returnDropAddr,
      add: returnDropAddr,
      description: returnDropAddr,
      lat: returnDropLat,
      lng: returnDropLng,
      coords: { lat: returnDropLat, lng: returnDropLng }
    };

    const returnItem = {
      ...b,
      id: `${b.id}_return`,
      bookingId: b.id,
      linkedBookingId: b.linkedBookingId || `${b.id}_outbound`,
      reference: returnRef || b.reference,
      tripdate: returnTripDateStr,
      bookingDate: returnTripDateStr,
      scheduledAt: returnTripDateStr,
      pickupAddress: returnPickupAddr,
      dropAddress: returnDropAddr,
      pickup: returnPickup,
      drop: returnDrop,
      estimateDistance: b.returnTrip?.distanceKm || b.returnTrip?.distance || b.estimateDistance,
      estimateTime: b.returnTrip?.durationSec || b.returnTrip?.durationMin ? (b.returnTrip.durationSec || b.returnTrip.durationMin * 60) : b.estimateTime,
      leg: "return",
      isReturn: true,
      hasReturn: true,
      legLabel: "RETOUR",
      trip_cost: legPriceReturn,
      driver_share: legShareReturn !== null ? legShareReturn : b.driver_share,
      roundTripTotal: bookingTotal,
      outboundTripDate: b.tripdate,
      returnTripDate: returnTripDateStr,
      status: b.returnTrip?.status || b.status,
      trip_status: b.returnTrip?.trip_status || (b.status === "COMPLETED" ? "ACCEPTED" : b.trip_status),
      returnTrip: {
        ...(b.returnTrip || {}),
        enabled: false,
      },
      instructionData: b.instructionData ? {
        ...b.instructionData,
        isReturn: true,
        leg: "return",
        pickupFlightNumber: b.returnTrip?.pickupFlightNumber || b.returnTrip?.flightNumber || null,
        pickupTrainNumber: b.returnTrip?.pickupTrainNumber || b.returnTrip?.trainNumber || null,
        returnFlightNumber: null,
        returnTrainNumber: null,
      } : b.instructionData,
      flightNumber: b.returnTrip?.flightNumber || null,
      trainNumber: b.returnTrip?.trainNumber || null,
      pickupFlightNumber: b.returnTrip?.pickupFlightNumber || b.returnTrip?.flightNumber || null,
      pickupTrainNumber: b.returnTrip?.pickupTrainNumber || b.returnTrip?.trainNumber || null,
      vehicleNumber: b.vehicleNumber || b.vehicle_number || null,
      vehicle_number: b.vehicle_number || b.vehicleNumber || null,
      vehicleMake: b.vehicleMake || null,
      vehicleModel: b.vehicleModel || null,
      vehicleColor: b.vehicleColor || null,
      vehicleYear: b.vehicleYear || null,
    };
    result.push(returnItem);
  }
  return result;
};

/* =====================================================
   FETCH BOOKINGS
===================================================== */

export const fetchBookings = () => (dispatch) => {
  const { auth, bookingListRef, bookingsDriverRef, bookingsDriverIdRef, driverBookingsRef, database } = firebase;

  if (!bookingListRef) {
    console.warn("[bookinglistactions] DB not ready (bookingListRef null), skipping fetchBookings.");
    return;
  }

  dispatch({ type: FETCH_BOOKINGS, payload: null });

  const userInfo = store.getState().auth.profile;
  if (!userInfo || (!userInfo.uid && userInfo.usertype !== "admin")) {
    console.warn("[bookinglistactions] User not ready, skipping fetchBookings.");
    return;
  }

  const effectiveRole = getEffectiveUserRole(userInfo);
  const bookingsById = new Map();

  const ingestData = (data) => {
    if (!data || typeof data !== "object") return;
    Object.keys(data).forEach((id) => {
      if (data[id] && typeof data[id] === "object") {
        const b = normalizeBookingPayload(data[id]) || {};
        b.id = id;
        bookingsById.set(id, b);
      }
    });
  };

  const processAndDispatchBookings = () => {
    const rawBookings = Array.from(bookingsById.values()).map(b => ({ ...b }));
    const bookings = expandRoundTripBookings(rawBookings);
    const active = [];
    let tracked = null;

    const isApprovedDriver = Boolean(
      userInfo?.usertype === "driver" &&
      userInfo?.approved === true &&
      userInfo?.isActive !== false
    );
    const activeDriverRides = [];

    for (const b of bookings) {
      const st = norm(b.status);
      if (st === "NEW" || st === "ACCEPTED") {
        active.push(b);
      }
      if (isApprovedDriver) {
        const isAssigned = (
          b.driver === userInfo.uid ||
          b.driverId === userInfo.uid ||
          b.driverUid === userInfo.uid ||
          b.driver_uid === userInfo.uid ||
          b.driver?.uid === userInfo.uid
        );
        if (isAssigned && isDriverRideInProgress(b)) {
          if (!activeDriverRides.some(ar => (ar.id || ar.bookingId) === (b.id || b.bookingId))) {
            activeDriverRides.push(b);
          }
        }
      }
    }

    if (activeDriverRides.length === 1) {
      tracked = activeDriverRides[0];
      fetchBookingLocations(tracked.bookingId || tracked.id)(dispatch);
    } else if (activeDriverRides.length > 1) {
      console.warn(`[bookinglistactions] Multi-course active conflict detected (${activeDriverRides.length} active rides). Freezing GPS tracking.`);
      tracked = null;
    }

    active.sort((a, b) => {
      const timeA = getBookingDateMs(a);
      const timeB = getBookingDateMs(b);
      if (timeA && timeB) {
        if (timeA === timeB) {
          if (a.isReturn && !b.isReturn) return 1;
          if (!a.isReturn && b.isReturn) return -1;
          return 0;
        }
        return timeA - timeB;
      }
      return timeA ? -1 : 1;
    });

    const sortedBookings = [...bookings].sort((a, b) => {
      const timeA = getBookingDateMs(a);
      const timeB = getBookingDateMs(b);
      if (timeA && timeB) {
        if (timeA === timeB) {
          if (a.isReturn && !b.isReturn) return 1;
          if (!a.isReturn && b.isReturn) return -1;
          return 0;
        }
        return timeB - timeA;
      }
      return timeB ? 1 : -1;
    });

    dispatch({
      type: FETCH_BOOKINGS_SUCCESS,
      payload: {
        bookings: sortedBookings,
        active,
        tracked,
      },
    });
  };

  const loadViaRest = async () => {
    let loaded = false;
    try {
      const { getValidAuthToken } = require("./authactions");
      let token = await getValidAuthToken();
      if (token && userInfo?.uid) {
        const baseUrl = (firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
        const urls = [];
        if (effectiveRole === "driver") {
          urls.push(`${baseUrl}/driver_bookings/${userInfo.uid}.json?auth=${token}`);
          urls.push(`${baseUrl}/bookings.json?auth=${token}&orderBy="driver"&equalTo="${userInfo.uid}"`);
          urls.push(`${baseUrl}/bookings.json?auth=${token}&orderBy="driverId"&equalTo="${userInfo.uid}"`);
        } else if (effectiveRole === "admin") {
          urls.push(`${baseUrl}/bookings.json?auth=${token}`);
        } else if (effectiveRole === "fleetadmin") {
          urls.push(`${baseUrl}/bookings.json?auth=${token}&orderBy="fleetadmin"&equalTo="${userInfo.uid}"`);
        } else {
          urls.push(`${baseUrl}/bookings.json?auth=${token}&orderBy="customer_uid"&equalTo="${userInfo.uid}"`);
          if (userInfo.usertype === "driver" && userInfo.approved === true) {
            urls.push(`${baseUrl}/driver_bookings/${userInfo.uid}.json?auth=${token}`);
          }
        }

        for (const u of urls) {
          try {
            let res = await fetch(u);
            if (res.status === 401) {
              token = await getValidAuthToken(true);
              if (token) {
                res = await fetch(u.replace(/auth=[^&]+/, `auth=${token}`));
              }
            }
            if (res.ok) {
              const data = await res.json();
              if (data && typeof data === 'object') {
                ingestData(data);
                loaded = true;
              }
            }
          } catch (_) {}
        }

        if (loaded) {
          processAndDispatchBookings();
        } else if (bookingsById.size === 0) {
          dispatch({
            type: FETCH_BOOKINGS_SUCCESS,
            payload: {
              bookings: [],
              active: [],
              tracked: null,
            },
          });
        }
      }
    } catch (e) {
      console.warn("[bookinglistactions] REST fallback error:", e?.message);
    }
    return loaded;
  };

  if (!auth?.currentUser && bookingListRef) {
    loadViaRest();
    return;
  }

  const qDriverBookings = driverBookingsRef ? driverBookingsRef(userInfo.uid) : (bookingListRef ? bookingListRef(userInfo.uid, "driver") : null);
  const qBookingsDriver = bookingsDriverRef ? bookingsDriverRef(userInfo.uid) : (database ? query(ref(database, "bookings"), orderByChild("driver"), equalTo(userInfo.uid)) : null);
  const qBookingsDriverId = bookingsDriverIdRef ? bookingsDriverIdRef(userInfo.uid) : (database ? query(ref(database, "bookings"), orderByChild("driverId"), equalTo(userInfo.uid)) : null);

  try { if (qDriverBookings) off(qDriverBookings); } catch (_) {}
  try { if (qBookingsDriver) off(qBookingsDriver); } catch (_) {}
  try { if (qBookingsDriverId) off(qBookingsDriverId); } catch (_) {}
  try { if (bookingListRef && userInfo.uid) off(bookingListRef(userInfo.uid, "customer")); } catch (_) {}
  try { if (bookingListRef && userInfo.uid) off(bookingListRef(userInfo.uid, effectiveRole)); } catch (_) {}

  if (effectiveRole === "driver") {
    let streamsDone = 0;
    const totalStreams = [qDriverBookings, qBookingsDriver, qBookingsDriverId].filter(Boolean).length || 1;
    const onStreamResult = () => {
      streamsDone++;
      if (bookingsById.size > 0) {
        processAndDispatchBookings();
      } else if (streamsDone >= totalStreams) {
        loadViaRest().then(ok => {
          if (!ok && bookingsById.size === 0) {
            dispatch({
              type: FETCH_BOOKINGS_SUCCESS,
              payload: { bookings: [], active: [], tracked: null },
            });
          }
        });
      }
    };

    if (qDriverBookings) {
      onValue(qDriverBookings, (snap) => {
        if (snap.val()) ingestData(snap.val());
        onStreamResult();
      }, (err) => {
        console.warn("[bookinglistactions] qDriverBookings error:", err?.message);
        onStreamResult();
      });
    }

    if (qBookingsDriver) {
      onValue(qBookingsDriver, (snap) => {
        if (snap.val()) ingestData(snap.val());
        onStreamResult();
      }, (err) => {
        console.warn("[bookinglistactions] qBookingsDriver error:", err?.message);
        onStreamResult();
      });
    }

    if (qBookingsDriverId) {
      onValue(qBookingsDriverId, (snap) => {
        if (snap.val()) ingestData(snap.val());
        onStreamResult();
      }, (err) => {
        console.warn("[bookinglistactions] qBookingsDriverId error:", err?.message);
        onStreamResult();
      });
    }
  } else {
    onValue(bookingListRef(userInfo.uid, effectiveRole), (snapshot) => {
      if (!snapshot.val()) {
        loadViaRest().then(ok => {
          if (!ok && bookingsById.size === 0) {
            dispatch({
              type: FETCH_BOOKINGS_FAILED,
              payload: store.getState().languagedata.defaultLanguage.no_bookings,
            });
          }
        });
        return;
      }
      ingestData(snapshot.val());
      processAndDispatchBookings();
    }, async (error) => {
      console.warn("[bookinglistactions] DB error, trying REST:", error?.message);
      const ok = await loadViaRest();
      if (!ok && bookingsById.size === 0) {
        dispatch({
          type: FETCH_BOOKINGS_FAILED,
          payload: error.message,
        });
      }
    });

    if (userInfo.usertype === "driver" && userInfo.approved === true && qDriverBookings) {
      onValue(qDriverBookings, (snap) => {
        if (snap.val()) {
          ingestData(snap.val());
          processAndDispatchBookings();
        }
      }, (err) => {
        console.warn("[bookinglistactions] qDriverBookings (customer mode) error:", err?.message);
      });
    }
  }
};

export const fetchSingleBooking = (bookingId) => async (dispatch) => {
  const isReturnRequested = typeof bookingId === "string" && bookingId.endsWith("_return");
  const isOutboundRequested = typeof bookingId === "string" && bookingId.endsWith("_outbound");
  const realId = String(bookingId || "").replace(/_(outbound|return)$/, "");
  const { singleBookingRef, singleDriverBookingRef, database } = firebase;
  const currentUserProfile = store.getState()?.auth?.profile;

  // 0. Dedicated operational view for drivers (/driver_bookings/{driverUid}/{bookingId})
  if (getEffectiveUserRole(currentUserProfile) === "driver" && currentUserProfile?.uid && singleDriverBookingRef) {
    try {
      const drvSnap = await get(singleDriverBookingRef(currentUserProfile.uid, bookingId));
      if (drvSnap.exists()) {
        const raw = drvSnap.val();
        raw.id = bookingId;
        return { success: true, data: raw };
      }
    } catch (_) { }
  }

  // 1. Direct lookup for natively split bookings in RTDB
  let snapshot = await get(singleBookingRef(bookingId));
  if (snapshot.exists()) {
    const raw = snapshot.val();
    raw.id = bookingId;
    return { success: true, data: raw };
  }

  // 2. Fallback to legacy unsplit parent booking
  if (realId !== bookingId) {
    snapshot = await get(singleBookingRef(realId));
    if (snapshot.exists()) {
      const raw = snapshot.val();
      raw.id = realId;
      const expanded = expandRoundTripBookings([raw]);
      if (isReturnRequested) {
        const returnLeg = expanded.find((x) => x.leg === "return") || expanded[0];
        return { success: true, data: returnLeg };
      }
      if (isOutboundRequested) {
        const outboundLeg = expanded.find((x) => x.leg === "outbound") || expanded[0];
        return { success: true, data: outboundLeg };
      }
      return { success: true, data: expanded[0] || raw };
    }
  }

  // 3. Fallback: Query RTDB by reference or bundleId (for references like D2G-XXXXXX, D2G-XXXXXX-O, D2G-XXXXXX-R)
  const db = database;
  if (db && typeof bookingId === "string") {
    const cleanId = bookingId.trim();

    // 3a. Exact reference match
    try {
      const refSnap = await get(query(ref(db, "bookings"), orderByChild("reference"), equalTo(cleanId)));
      if (refSnap.exists()) {
        const val = refSnap.val();
        const keys = val ? Object.keys(val) : [];
        if (keys.length > 0) {
          const raw = val[keys[0]];
          raw.id = keys[0];
          return { success: true, data: raw };
        }
      }
    } catch (e) {
      console.warn("[fetchSingleBooking] Query by reference error:", e?.message);
    }

    // 3b. Base reference without -O / -R suffix (e.g. D2G-750903) -> query -O then -R
    const isBaseRef = /^D2G-[A-Za-z0-9]+$/i.test(cleanId) && !cleanId.endsWith("-O") && !cleanId.endsWith("-R");
    if (isBaseRef) {
      try {
        const outSnap = await get(query(ref(db, "bookings"), orderByChild("reference"), equalTo(`${cleanId}-O`)));
        if (outSnap.exists()) {
          const outVal = outSnap.val();
          const outKeys = outVal ? Object.keys(outVal) : [];
          if (outKeys.length > 0) {
            const raw = outVal[outKeys[0]];
            raw.id = outKeys[0];
            return { success: true, data: raw };
          }
        }
        const retSnap = await get(query(ref(db, "bookings"), orderByChild("reference"), equalTo(`${cleanId}-R`)));
        if (retSnap.exists()) {
          const retVal = retSnap.val();
          const retKeys = retVal ? Object.keys(retVal) : [];
          if (retKeys.length > 0) {
            const raw = retVal[retKeys[0]];
            raw.id = retKeys[0];
            return { success: true, data: raw };
          }
        }
      } catch (e) {
        console.warn("[fetchSingleBooking] Fallback base reference query error:", e?.message);
      }
    }

    // 3c. Query by bundleId
    try {
      const bundleSnap = await get(query(ref(db, "bookings"), orderByChild("bundleId"), equalTo(cleanId)));
      if (bundleSnap.exists()) {
        const bVal = bundleSnap.val();
        const bKeys = bVal ? Object.keys(bVal) : [];
        if (bKeys.length > 0) {
          const preferredKey = bKeys.find(k => bVal[k].leg === "outbound" || !bVal[k].isReturn || (typeof bVal[k].reference === "string" && bVal[k].reference.endsWith("-O"))) || bKeys[0];
          const raw = bVal[preferredKey];
          raw.id = preferredKey;
          return { success: true, data: raw };
        }
      }
    } catch (e) {
      console.warn("[fetchSingleBooking] Query by bundleId error:", e?.message);
    }
  }

  return { success: false };
};

/* =====================================================
   UPDATE BOOKING
===================================================== */

export const safeUpdateBookingData = async (bookingId, payload, singleBookingRef) => {
  const currentUserProfile = store.getState()?.auth?.profile;
  if (currentUserProfile?.usertype === "driver") {
    try {
      const { getValidAuthToken } = require("./authactions");
      const token = await getValidAuthToken();
      if (token) {
        const projectId = firebase.config?.projectId || "drive2go-c806b";
        const region = firebase.config?.functionsRegion || "europe-west1";
        const baseUrl = (typeof process !== "undefined" && process.env?.REACT_APP_FUNCTIONS_URL)
          ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, "")
          : `https://${region}-${projectId}.cloudfunctions.net`;
        const res = await fetch(`${baseUrl}/updateDriverBookingStatus`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ bookingId, ...payload })
        });
        if (res.ok) {
          return true;
        } else {
          let errData = {};
          try { errData = await res.json(); } catch (_) {}
          const err = new Error(errData.message || "Échec de mise à jour du statut par le serveur.");
          err.code = errData.error || "UPDATE_DRIVER_STATUS_FAILED";
          err.remainingAttempts = errData.remainingAttempts;
          throw err;
        }
      }
    } catch (drvUpdErr) {
      if (drvUpdErr?.code || drvUpdErr?.remainingAttempts !== undefined) {
        throw drvUpdErr;
      }
      console.warn("[safeUpdateBookingData] updateDriverBookingStatus error:", drvUpdErr?.message);
    }
  }

  let writeSuccess = false;
  if (singleBookingRef) {
    try {
      await Promise.race([
        update(singleBookingRef(bookingId), payload),
        new Promise((_, reject) => setTimeout(() => reject(new Error("SDK_TIMEOUT")), 3000))
      ]);
      writeSuccess = true;
    } catch (writeErr) {
      console.warn("[safeUpdateBookingData] SDK write warning:", writeErr?.message);
    }
  }

  if (!writeSuccess) {
    try {
      const { getValidAuthToken } = require("./authactions");
      let token = await getValidAuthToken();
      if (token) {
        const dbUrl = (firebase.config?.databaseURL || "https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app").replace(/\/$/, "");
        let res = await fetch(`${dbUrl}/bookings/${bookingId}.json?auth=${token}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (res.status === 401 || res.status === 403) {
          token = await getValidAuthToken(true);
          if (token) {
            res = await fetch(`${dbUrl}/bookings/${bookingId}.json?auth=${token}`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            });
          }
        }
        if (res.ok) {
          writeSuccess = true;
        } else {
          console.error("[safeUpdateBookingData] REST error:", res.status, await res.text());
        }
      }
    } catch (restErr) {
      console.error("[safeUpdateBookingData] REST fallback error:", restErr?.message);
    }
  }
  return writeSuccess;
};

export const rollbackOrResyncBooking = async (realId, previousClone, mutationId, dispatch, singleBookingRef) => {
  const freshStoreState = store.getState()?.bookinglistdata;
  const liveBooking = freshStoreState?.bookings?.find((item) => (item.id === realId || item.bookingId === realId))
    || freshStoreState?.active?.find((item) => (item.id === realId || item.bookingId === realId))
    || (freshStoreState?.tracked?.id === realId ? freshStoreState.tracked : null);

  // Rollback ONLY if the current store state still reflects our specific optimistic mutation.
  // If remote RTDB or a newer local action updated the booking (even if status is unchanged, e.g. driver changed),
  // _optimisticMutationId will not match -> rollback is skipped!
  const hasMatchingMutation = liveBooking && (liveBooking._optimisticMutationId === mutationId);

  if (previousClone) {
    if (hasMatchingMutation) {
      const { _optimisticMutationId: _unused, ...cleanPrevious } = previousClone;
      dispatch({ type: UPDATE_BOOKING, payload: cleanPrevious });
    } else {
      console.warn(`[updateBooking] Skipping rollback for ${realId}: store already received a newer update or different mutation (live mutationId=${liveBooking?._optimisticMutationId}, expected=${mutationId})`);
    }
  } else if (singleBookingRef) {
    // Fallback: previousClone was absent, resynchronize directly from remote RTDB
    try {
      const snap = await get(singleBookingRef(realId));
      if (snap.exists()) {
        dispatch({ type: UPDATE_BOOKING, payload: { ...snap.val(), id: realId, bookingId: realId } });
      } else {
        // Deleted remotely on RTDB
        dispatch({ type: CANCEL_BOOKING_SUCCESS, payload: { id: realId, bookingId: realId } });
      }
    } catch (resyncErr) {
      console.warn(`[updateBooking] Remote resync failed for ${realId}:`, resyncErr?.message);
    }
  }
};

export const updateBooking = (booking) => async (dispatch) => {
  const {
    auth,
    trackingRef,
    singleBookingRef,
    singleUserRef,
    settingsRef,
    userRatingsRef,
  } = firebase;

  const realId = booking.id || booking.bookingId;
  const sanitizedBooking = { ...booking, id: realId };
  const mutationId = `mut_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  // [LOT-3] Capture previous booking state before optimistic dispatch for rollback if server write fails
  const currentStoreState = store.getState()?.bookinglistdata;
  const previousBooking = currentStoreState?.bookings?.find((item) => (item.id === realId || item.bookingId === realId))
    || currentStoreState?.active?.find((item) => (item.id === realId || item.bookingId === realId))
    || (currentStoreState?.tracked && (currentStoreState.tracked.id === realId || currentStoreState.tracked.bookingId === realId) ? currentStoreState.tracked : null);
  const previousClone = previousBooking ? JSON.parse(JSON.stringify(previousBooking)) : null;

  const optimisticBooking = { ...sanitizedBooking, _optimisticMutationId: mutationId };
  dispatch({ type: UPDATE_BOOKING, payload: optimisticBooking });

  // Work on a copy to avoid unintended mutations
  let b = syncLegacyPaymentMirrors(sanitizedBooking);

  // [FIX] Ensure driver field is a string (UID) to pass RTDB indexing and security rules.
  // Because normalizeBookingPayload turns it into an object, we must revert it before write.
  if (b.driver && typeof b.driver === 'object') {
    if (b.driver.uid) {
      b.driver = b.driver.uid;
    } else {
      // Logic for partial driver object that might be missing uid (unlikely but safe)
      b.driver = booking.driverUid || b.driver_uid || b.driver;
    }
  }

  // [FIX] Sanitize driverUid too if it became an object
  if (b.driverUid && typeof b.driverUid === 'object') {
      b.driverUid = b.driverUid.uid || b.driver;
  }

  // [FIX] Sanitize tripdate if it's a moment/dayjs object or complex structure
  if (b.tripdate && typeof b.tripdate === 'object') {
    if (b.tripdate.format) { // Day.js or Moment.js
       b.tripdate = b.tripdate.format();
    } else if (b.tripdate._isAMomentObject || b.tripdate.$d) {
       b.tripdate = b.tripdate.valueOf();
    }
  }

  // [FIX] Sanitize returnDate if it's a moment/dayjs object or complex structure
  if (b.returnDate && typeof b.returnDate === 'object') {
    if (b.returnDate.format) {
       b.returnDate = b.returnDate.format();
    } else if (b.returnDate._isAMomentObject || b.returnDate.$d) {
       b.returnDate = b.returnDate.valueOf();
    }
  }

  const incomingStatus = norm(b.status);
  const currentStatus = norm(booking.status);

  // [STATE_MACHINE] Final state guard: No transitions allowed from COMPLETED or CANCELLED
  if ((currentStatus === "COMPLETED" || currentStatus === "CANCELLED") && incomingStatus !== currentStatus) {
    // Allow admin override with explicit flag (used by restore/modify actions)
    if (b._adminOverride) {
      console.log(`[updateBooking] ADMIN OVERRIDE: ${currentStatus} → ${incomingStatus}`);
      delete b._adminOverride;
    } else {
      // Exception: Allow admin to update fields without changing status, but if status change is attempted, block it.
      console.warn(`[updateBooking] BLOCKED: Cannot change status from ${currentStatus} to ${incomingStatus}`);
      // We modify b to keep the original status to allow updating other fields (payment, rating, etc)
      b.status = currentStatus;
    }
  }

  /* ----------------------------
     [STATE_MACHINE] TRIP PROGRESS
     Move ARRIVED/STARTED/REACHED out of booking.status into booking.trip_status
  ----------------------------- */

  if (incomingStatus === "ARRIVED") {
    b.trip_status = "ARRIVED";
    b.status = "ACCEPTED";
    b.driver_arrive_time = Date.now().toString();
    b = syncLegacyPaymentMirrors(b);

    const minimalPayload = {
      trip_status: "ARRIVED",
      driver_arrive_time: b.driver_arrive_time,
    };
    await safeUpdateBookingData(b.id, minimalPayload, singleBookingRef);

    dispatch({
      type: UPDATE_BOOKING,
      payload: {
        id: b.id,
        bookingId: b.id,
        status: "ACCEPTED",
        trip_status: "ARRIVED",
        driver_arrive_time: b.driver_arrive_time,
      },
    });

    if (b.customer_token) {
      RequestPushMsg(b.customer_token, {
        title: store.getState().languagedata.defaultLanguage.notification_title,
        msg: store.getState().languagedata.defaultLanguage.driver_near,
        screen: "BookedCab",
        params: { bookingId: b.id },
      });
    }
    return;
  }

  if (incomingStatus === "STARTED") {
    b.trip_status = "STARTED";
    b.status = "STARTED";

    const dt = new Date();
    b.trip_start_time = `${dt.getHours()}:${dt.getMinutes()}:${dt.getSeconds()}`;
    b.startTime = dt.getTime();

    // Payload minimal pour respecter les règles de sécurité RTDB du chauffeur
    const minimalPayload = {
      status: "STARTED",
      trip_status: "STARTED",
      trip_start_time: b.trip_start_time,
      startTime: b.startTime,
      ...(b.enteredOtp !== undefined ? { otp: b.enteredOtp } : (b.otp !== undefined ? { otp: b.otp } : {}))
    };

    try {
      const writeSuccess = await safeUpdateBookingData(b.id, minimalPayload, singleBookingRef);
      if (!writeSuccess) {
        const err = new Error("Impossible de démarrer la course (PERMISSION_DENIED).");
        err.code = "PERMISSION_DENIED";
        throw err;
      }
    } catch (writeErr) {
      // [LOT-3] Rollback Redux to previous state if server rejects or network fails (without overwriting newer updates)
      await rollbackOrResyncBooking(realId, previousClone, mutationId, dispatch, singleBookingRef);
      throw writeErr;
    }

    dispatch({
      type: UPDATE_BOOKING,
      payload: {
        id: b.id,
        bookingId: b.id,
        status: "STARTED",
        trip_status: "STARTED",
        trip_start_time: b.trip_start_time,
        startTime: b.startTime,
      },
    });

    const driverLocation = store.getState().gpsdata.location;
    if (driverLocation?.lat != null && driverLocation?.lng != null) {
      try {
        push(trackingRef(b.id), {
          at: Date.now(),
          status: "STARTED",
          lat: driverLocation.lat,
          lng: driverLocation.lng,
        });
      } catch (trackErr) {
        console.warn("[updateBooking] trackingRef push non-bloquant:", trackErr?.message);
      }
    }

    if (b.customer_token) {
      RequestPushMsg(b.customer_token, {
        title: store.getState().languagedata.defaultLanguage.notification_title,
        msg: store.getState().languagedata.defaultLanguage.driver_journey_msg + (b.reference || ""),
        screen: "BookedCab",
        params: { bookingId: b.id },
      });
    }
    return;
  }

  if (incomingStatus === "REACHED") {
    b.trip_status = "REACHED";
    b.status = "COMPLETED";

    const driverLocation = store.getState().gpsdata.location;
    if (driverLocation?.lat != null && driverLocation?.lng != null) {
      try {
        push(trackingRef(b.id), {
          at: Date.now(),
          status: "REACHED",
          lat: driverLocation.lat,
          lng: driverLocation.lng,
        });
      } catch (_) {}
    }

    // [FIX] Fallback location for Web Dashboard usage (where GPS is missing)
    const finalLocation = (driverLocation && driverLocation.lat) ? driverLocation : {
      lat: b.drop?.lat || 0,
      lng: b.drop?.lng || 0
    };

    // [FIX] Fallback start time if missing (Direct completion)
    if (!b.startTime) {
      b.startTime = Date.now().toString();
    }

    // [DEBUG] REACHED FLOW START
    let address = null;
    try {
      address = await saveAddresses(b, finalLocation);
    } catch (addrErr) {
      console.warn("[REACHED] saveAddresses failed (expected for driver):", addrErr.message);
    }
    let bookingObj;
    try {
      bookingObj = await addActualsToBooking(b, address, finalLocation);
    } catch (actErr) {
      console.warn("[REACHED] addActualsToBooking failed:", actErr.message);
      bookingObj = { ...b };
    }

    bookingObj.trip_status = "REACHED";
    bookingObj.status = "COMPLETED";

    const minimalPayload = {
      status: "COMPLETED",
      trip_status: "REACHED",
    };

    await safeUpdateBookingData(b.id, minimalPayload, singleBookingRef);

    dispatch({
      type: UPDATE_BOOKING,
      payload: {
        id: b.id,
        bookingId: b.id,
        status: "COMPLETED",
        trip_status: "REACHED",
      },
    });

    if (b.customer_token) {
      RequestPushMsg(b.customer_token, {
        title: store.getState().languagedata.defaultLanguage.notification_title,
        msg: store.getState().languagedata.defaultLanguage.driver_completed_ride,
        screen: "BookedCab",
        params: { bookingId: b.id },
      });
    }
    return;
  }

  /* ----------------------------
     [STATE_MACHINE] PAYMENT LEGACY MAPPING
     Never write PAID/PENDING/PAYMENT_PENDING in booking.status
  ----------------------------- */

  if (incomingStatus === "PAYMENT_PENDING" || incomingStatus === "PENDING") {
    b.payment.status = "PENDING";
    b.payment_status = "PENDING";
    b.status = norm(b.status) === "ACCEPTED" ? "ACCEPTED" : "NEW";
    b = syncLegacyPaymentMirrors(b);
    await update(singleBookingRef(b.id), b);
    return;
  }

  if (incomingStatus === "PAID") {
    b.payment.status = "PAID";
    b.payment_status = "PAID";

    // if trip ended => completed, else accepted
    if (b.booking_from_web || norm(b.trip_status) === "REACHED" || norm(b.status) === "COMPLETED" || norm(b.status) === "REACHED") {
      b.status = "COMPLETED";
      b.trip_status = "REACHED";
    } else {
      b.status = "ACCEPTED";
    }

    b = syncLegacyPaymentMirrors(b);

    // [FIX] Use minimal payload to avoid PERMISSION_DENIED when driver updates payment
    const paidPayload = {
      status: b.status,
      trip_status: b.trip_status || "REACHED",
      payment: b.payment,
      payment_status: b.payment_status,
      payment_mode: b.payment_mode,
      customer_paid: b.customer_paid || b.trip_cost || 0,
      cashPaid: true,
      cashPaymentAmount: b.cashPaymentAmount || 0,
    };
    await safeUpdateBookingData(b.id, paidPayload, singleBookingRef);

    dispatch({
      type: UPDATE_BOOKING,
      payload: {
        id: b.id,
        bookingId: b.id,
        ...paidPayload,
      },
    });

    if (
      b.driver === auth.currentUser?.uid &&
      (b.prepaid || b.payment_mode === "cash" || b.payment_mode === "wallet")
    ) {
      update(singleUserRef(b.driver), { queue: false }).catch(() => {});
    }

    if (b.customer_token) {
      RequestPushMsg(b.customer_token, {
        title: store.getState().languagedata.defaultLanguage.notification_title,
        msg: store.getState().languagedata.defaultLanguage.success_payment,
        screen: "BookedCab",
        params: { bookingId: b.id },
      });
    }
    if (b.driver_token) {
      RequestPushMsg(b.driver_token, {
        title: store.getState().languagedata.defaultLanguage.notification_title,
        msg: store.getState().languagedata.defaultLanguage.success_payment,
        screen: "BookedCab",
        params: { bookingId: b.id },
      });
    }
    return;
  }

  if (incomingStatus === "REFUNDED") {
    b.payment.status = "REFUNDED";
    b.payment_status = "REFUNDED";
    b.status = "CANCELLED";
    b = syncLegacyPaymentMirrors(b);
    const cleanedData = stripUndefinedDeep(b);
    await update(singleBookingRef(b.id), cleanedData);
    return { success: true };
  }

  if (incomingStatus === "COMPLETE") {
    b.status = "COMPLETED";
  }

  // Canonical booking statuses only
  const canonicalStatus = norm(b.status);
  if (!["NEW", "ACCEPTED", "COMPLETED", "CANCELLED"].includes(canonicalStatus)) {
    b.status = "NEW";
  }

  // Populate driverAssignedAt if driver is assigned
  if (norm(b.status) === "ACCEPTED" && b.driver && !b.driverAssignedAt) {
    b.driverAssignedAt = new Date().toISOString();
  }

  // Apply driver queue update on NEW/ACCEPTED
  if (norm(b.status) === "NEW" || norm(b.status) === "ACCEPTED") {
    const sanitized = stripUndefinedDeep(updateDriverQueue(syncLegacyPaymentMirrors(b)));
    try {
      const writeOk = await safeUpdateBookingData(b.id, sanitized, singleBookingRef);
      if (!writeOk) {
        const err = new Error("Impossible de mettre à jour la réservation (PERMISSION_DENIED).");
        err.code = "PERMISSION_DENIED";
        throw err;
      }
    } catch (writeErr) {
      if (previousClone) {
        dispatch({ type: UPDATE_BOOKING, payload: previousClone });
      }
      throw writeErr;
    }
    return { success: true };
  }

  // Completed/CANCELLED writes
  if (norm(b.status) === "CANCELLED") {
    b = syncLegacyPaymentMirrors(b);
    // [FIX] Minimal payload for CANCELLED to avoid PERMISSION_DENIED
    const cancelPayload = {
      status: "CANCELLED",
      reason: b.reason || null,
      cancelledBy: b.cancelledBy || null,
      payment: b.payment,
      payment_status: b.payment_status,
      payment_mode: b.payment_mode,
      cancelledAt: b.cancelledAt || new Date().toISOString(),
    };
    try {
      await safeUpdateBookingData(b.id, cancelPayload, singleBookingRef);
    } catch (writeErr) {
      await rollbackOrResyncBooking(realId, previousClone, mutationId, dispatch, singleBookingRef);
      throw writeErr;
    }
    return;
  }

  if (norm(b.status) === "COMPLETED") {
    b.status = "COMPLETED";
    b = syncLegacyPaymentMirrors(b);
    // [FIX] Minimal payload for COMPLETED to avoid PERMISSION_DENIED
    const completedPayload = {
      status: "COMPLETED",
      payment: b.payment,
      payment_status: b.payment_status,
      payment_mode: b.payment_mode,
      customer_paid: b.customer_paid || 0,
      cashPaid: b.cashPaid || false,
      cashPaymentAmount: b.cashPaymentAmount || 0,
      completedAt: b.completedAt || new Date().toISOString(),
    };
    try {
      await safeUpdateBookingData(b.id, completedPayload, singleBookingRef);
    } catch (writeErr) {
      await rollbackOrResyncBooking(realId, previousClone, mutationId, dispatch, singleBookingRef);
      throw writeErr;
    }

    dispatch({
      type: UPDATE_BOOKING,
      payload: {
        id: b.id,
        bookingId: b.id,
        ...completedPayload,
      },
    });

    // Rating flow (kept)
    if (b.rating) {
      if (b.driver_token) {
        RequestPushMsg(b.driver_token, {
          title: store.getState().languagedata.defaultLanguage.notification_title,
          msg: store.getState().languagedata.defaultLanguage.received_rating
            .toString()
            .replace("X", b.rating.toString()),
          screen: "BookedCab",
          params: { bookingId: b.id },
        });
      }

      onValue(
        userRatingsRef(b.driver),
        (snapshot) => {
          let ratings = snapshot.val();
          let rating;
          if (ratings) {
            let sum = 0;
            const arr = Object.values(ratings);
            for (let i = 0; i < arr.length; i++) sum = sum + arr[i].rate;
            sum = sum + b.rating;
            rating = parseFloat(sum / (arr.length + 1)).toFixed(1);
          } else {
            rating = b.rating;
          }
          // [FIX] Direct driver profile write on /users/<driverUid> requires admin privileges.
          // Customers record their reviews directly in userRatingsRef below.
          const currentUserUid = auth.currentUser?.uid;
          const isAdmin = store.getState().auth?.profile?.usertype === 'admin';
          if (b.driver && isAdmin) {
            update(singleUserRef(b.driver), { rating: rating }).catch(() => {});
          }
          push(userRatingsRef(b.driver), {
            user: b.customer,
            rate: b.rating,
            bookingId: b.id,
          }).catch((err) => console.warn("[Rating] push userRatings failed:", err));
        },
        { onlyOnce: true }
      );
    }
    return;
  }

  // Fallback safe write
  b.status = "NEW";
  b = syncLegacyPaymentMirrors(b);
  try {
    await safeUpdateBookingData(b.id, b, singleBookingRef);
  } catch (writeErr) {
    await rollbackOrResyncBooking(realId, previousClone, mutationId, dispatch, singleBookingRef);
    throw writeErr;
  }
};

/* =====================================================
   CANCEL BOOKING
===================================================== */

export const cancelBooking = (data) => async (dispatch) => {
  const { singleBookingRef, singleUserRef, requestedDriversRef } = firebase;

  const realId = data.booking?.id || data.booking?.bookingId;
  const sanitizedBooking = { ...data.booking, id: realId };
  const sanitizedData = { ...data, booking: sanitizedBooking };

  dispatch({ type: CANCEL_BOOKING, payload: sanitizedData });

  const currentStatus = norm(data.booking.status);
  if (currentStatus === "COMPLETED" || currentStatus === "CANCELLED") {
    console.warn("[cancelBooking] BLOCKED: Cannot cancel a booking in a final state:", currentStatus);
    return { success: false, message: "Booking is already in a final state" };
  }

  const cancelPayload = {
    status: "CANCELLED",
    reason: data.reason,
    cancelledBy: data.cancelledBy,
    cancelledAt: new Date().toISOString()
  };

  let writeSuccess = false;

  try {
    await update(singleBookingRef(realId), cancelPayload);
    writeSuccess = true;
  } catch (sdkError) {
    console.warn("[cancelBooking] Firebase SDK direct update failed, attempting authenticated REST fallback:", sdkError?.message);

    try {
      const { getValidAuthToken } = require("./authactions");
      const token = await getValidAuthToken(true);
      if (token) {
        const baseUrl = (firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
        const rtdbUrl = `${baseUrl}/bookings/${realId}.json?auth=${token}`;
        const restRes = await fetch(rtdbUrl, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(cancelPayload)
        });
        if (restRes.ok) {
          writeSuccess = true;
          console.log("[cancelBooking] REST fallback write successful!");
        } else {
          console.warn("[cancelBooking] REST fallback returned HTTP", restRes.status);
        }
      }
    } catch (restErr) {
      console.warn("[cancelBooking] REST fallback error:", restErr?.message);
    }

    if (!writeSuccess) {
      try {
        const { getValidAuthToken } = require("./authactions");
        const token = await getValidAuthToken(true);
        const projectId = firebase.config?.projectId || "drive2go-c806b";
        const region = (typeof process !== "undefined" && (process.env?.REACT_APP_FUNCTIONS_REGION || process.env?.FUNCTIONS_REGION)) || firebase.config?.functionsRegion || "europe-west1";
        const cfUrl = (typeof process !== "undefined" && process.env?.REACT_APP_FUNCTIONS_URL)
          ? `${process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')}/cancelAuthenticatedBooking`
          : `https://${region}-${projectId}.cloudfunctions.net/cancelAuthenticatedBooking`;
        const cfRes = await fetch(cfUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { "Authorization": `Bearer ${token}` } : {})
          },
          body: JSON.stringify({
            bookingId: realId,
            reason: data.reason,
            cancelledBy: data.cancelledBy
          })
        });
        const cfData = await cfRes.json().catch(() => null);
        if (cfRes.ok && cfData?.success) {
          writeSuccess = true;
          console.log("[cancelBooking] Cloud Function fallback succeeded!");
        } else {
          console.error("[cancelBooking] Cloud Function fallback failed:", cfRes.status, cfData);
        }
      } catch (cfErr) {
        console.error("[cancelBooking] Cloud Function invocation error:", cfErr?.message);
      }
    }

    if (!writeSuccess) {
      console.error("[cancelBooking] Error writing CANCELLED to Firebase across all fallbacks:", sdkError);
      throw sdkError;
    }
  }

  try {
    const st = norm(data.booking.status);
    const tripSt = norm(data.booking.trip_status);

    const driverId = (typeof data.booking.driver === 'object' && data.booking.driver !== null)
      ? (data.booking.driver.uid || data.booking.driver.id)
      : data.booking.driver;

    const currentUserUid = firebase?.auth?.currentUser?.uid || store.getState().auth?.profile?.uid;
    const isAdmin = store.getState().auth?.profile?.usertype === 'admin';
    if (
      driverId &&
      (driverId === currentUserUid || isAdmin) &&
      ((st === "NEW" || st === "ACCEPTED") || (tripSt === "ARRIVED" || tripSt === "STARTED"))
    ) {
      update(singleUserRef(driverId), { queue: false }).catch(() => {});
    }

    if (st === "NEW") {
      remove(requestedDriversRef(realId)).catch(() => {});
    }

    return { success: true };
  } catch (cleanUpErr) {
    console.warn("[cancelBooking] Post-cancellation cleanup warning:", cleanUpErr?.message);
    return { success: true };
  }
};
/* =====================================================
   RESTORE BOOKING (Admin only — reverses a cancellation)
===================================================== */

export const restoreBooking = (booking) => async (dispatch) => {
  const { singleBookingRef } = firebase;
  const realId = booking.id || booking.bookingId;

  const restoredBooking = {
    ...booking,
    id: realId,
    status: "ACCEPTED",
    _adminOverride: true,
    reason: null,
    cancelledBy: null,
    cancelledAt: null,
  };

  dispatch({ type: UPDATE_BOOKING, payload: restoredBooking });

  await update(singleBookingRef(realId), {
    status: "ACCEPTED",
    reason: null,
    cancelledBy: null,
    cancelledAt: null,
    "notifFlags/BOOKING_CANCELLED": null,
  });

  console.log(`[restoreBooking] Booking ${realId} restored to ACCEPTED`);
};

/* =====================================================
   UPDATE BOOKING IMAGE
===================================================== */

export const updateBookingImage = (booking, imageType, imageBlob) => (dispatch) => {
  const { singleBookingRef, bookingImageRef } = firebase;
  const realId = booking.id || booking.bookingId;

  uploadBytesResumable(bookingImageRef(realId, imageType), imageBlob)
    .then(() => {
      imageBlob.close();
      return getDownloadURL(bookingImageRef(realId, imageType));
    })
    .then((url) => {
      const b = { ...booking, id: realId };
      if (imageType === "pickup_image") b.pickup_image = url;
      if (imageType === "deliver_image") b.deliver_image = url;

      update(singleBookingRef(realId), b);

      dispatch({
        type: UPDATE_BOOKING,
        payload: b,
      });
    });
};

/* =====================================================
   FORCE END BOOKING
===================================================== */

export const forceEndBooking = (booking) => async (dispatch) => {
  const { trackingRef, singleBookingRef, singleUserRef, walletHistoryRef, settingsRef } = firebase;
  const realId = booking.id || booking.bookingId;

  const bookingToDispatch = { ...booking, id: realId };
  dispatch({ type: UPDATE_BOOKING, payload: bookingToDispatch });

  let b = syncLegacyPaymentMirrors({ ...bookingToDispatch });

  const tripSt = norm(b.trip_status);
  const canForceEnd = tripSt === "STARTED" || (norm(b.status) === "ACCEPTED" && b.startTime);
  if (!canForceEnd) return;

  push(trackingRef(realId), {
    at: Date.now(),
    status: "REACHED",
    lat: b.drop?.lat,
    lng: b.drop?.lng,
  });

  const end_time = new Date();
  const diff = (end_time.getTime() - parseFloat(b.startTime || end_time.getTime())) / 1000;
  const totalTimeTaken = Math.abs(Math.round(diff));

  b.trip_end_time = `${end_time.getHours()}:${end_time.getMinutes()}:${end_time.getSeconds()}}`;
  b.total_trip_time = totalTimeTaken;

  if (b.customer_token) {
    RequestPushMsg(b.customer_token, {
      title: store.getState().languagedata.defaultLanguage.notification_title,
      msg: store.getState().languagedata.defaultLanguage.driver_completed_ride,
      screen: "BookedCab",
      params: { bookingId: b.id },
    });
  }

  const currentUserUid = auth.currentUser?.uid;
  const isAdmin = store.getState().auth?.profile?.usertype === 'admin';
  if (b.driver && (b.driver === currentUserUid || isAdmin)) {
    update(singleUserRef(b.driver), { queue: false }).catch(() => {});
  }

  b.trip_status = "REACHED";
  b.status = "COMPLETED";

  if (b.prepaid) {
    const settings = (await get(settingsRef)).val();

    onValue(
      singleUserRef(b.driver),
      (snapshot) => {
        let walletBalance = parseFloat(snapshot.val().walletBalance);
        walletBalance = walletBalance + parseFloat(b.driver_share || 0);
        if (parseFloat(b.cashPaymentAmount || 0) > 0) {
          walletBalance = walletBalance - parseFloat(b.cashPaymentAmount);
        }
        if (isAdmin) {
          update(singleUserRef(b.driver), { walletBalance: parseFloat(walletBalance.toFixed(settings.decimal)) }).catch(() => {});
        }

        push(walletHistoryRef(b.driver), {
          type: "Credit",
          amount: parseFloat(b.driver_share || 0).toFixed(settings.decimal),
          date: Date.now(),
          txRef: b.id,
        });

        if (parseFloat(b.cashPaymentAmount || 0) > 0) {
          push(walletHistoryRef(b.driver), {
            type: "Debit",
            amount: b.cashPaymentAmount,
            date: Date.now(),
            txRef: b.id,
          });
        }
      },
      { onlyOnce: true }
    );

    b.payment.status = "PAID";
    b.payment_status = "PAID";

    if (b.customer_token) {
      RequestPushMsg(b.customer_token, {
        title: store.getState().languagedata.defaultLanguage.notification_title,
        msg: store.getState().languagedata.defaultLanguage.success_payment,
        screen: "BookedCab",
        params: { bookingId: b.id },
      });
    }

    if (b.driver_token) {
      RequestPushMsg(b.driver_token, {
        title: store.getState().languagedata.defaultLanguage.notification_title,
        msg: store.getState().languagedata.defaultLanguage.success_payment,
        screen: "BookedCab",
        params: { bookingId: b.id },
      });
    }
  } else {
    b.payment.status = "PENDING";
    b.payment_status = "PENDING";
    b.payment.mode = "ONBOARD";
    b.payment.provider = null;
  }

  b = syncLegacyPaymentMirrors(b);
  update(singleBookingRef(b.id), b);
};

/* =====================================================
   GUEST RECOVERY (MAGIC LINK)
===================================================== */

export const fetchGuestBooking = (bookingId, token) => async (dispatch) => {
  const { config } = firebase;
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
  const functionsUrl = (process && process.env && process.env.REACT_APP_FUNCTIONS_URL)
    ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
    : null;
  let host = origin || functionsUrl || `https://${config?.projectId}.web.app`;
  let url = `${host}/getGuestBooking?bookingId=${bookingId}&token=${token}`;

  try {
    const response = await fetch(url);
    const json = await response.json();
    return json;
  } catch (e) {
    console.error("[fetchGuestBooking] error", e);
    return { success: false, error: e.message };
  }
};

/**
 * [AUTH_RECOVERY] Switch an authenticated user's booking to Onboard payment
 * via Cloud Function (admin privileges bypass RTDB rules).
 */
export const switchToOnboard = (bookingId) => async (dispatch) => {
  const { config } = firebase;
  const functionsUrl = (typeof process !== "undefined" && process?.env?.REACT_APP_FUNCTIONS_URL)
    ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
    : null;
  const region = (typeof process !== "undefined" && (process.env?.REACT_APP_FUNCTIONS_REGION || process.env?.FUNCTIONS_REGION)) || firebase.config?.functionsRegion || "europe-west1";
  const cfUrl = functionsUrl
    ? `${functionsUrl}/switchToOnboard`
    : `https://${region}-${config?.projectId || firebase.config?.projectId || "drive2go-c806b"}.cloudfunctions.net/switchToOnboard`;
  try {
    const { getValidAuthToken } = require("./authactions");
    const token = await getValidAuthToken(true);
    const response = await fetch(cfUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "Authorization": `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ bookingId })
    });
    const json = await response.json().catch(() => null);
    if (response.ok && json?.success) {
      // Update local Redux state with the returned booking
      if (json.booking) {
        dispatch({ type: UPDATE_BOOKING, payload: json.booking });
      }
      return { success: true, booking: json.booking };
    }
    const errMsg = json?.message || json?.error || "Switch to onboard failed";
    throw new Error(errMsg);
  } catch (e) {
    console.error("[switchToOnboard] error", e);
    throw e;
  }
};

export const payBookingWithWallet = (bookingId) => async (dispatch) => {
  const { config } = firebase;
  const functionsUrl = (typeof process !== "undefined" && process?.env?.REACT_APP_FUNCTIONS_URL)
    ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
    : null;
  const region = (typeof process !== "undefined" && (process.env?.REACT_APP_FUNCTIONS_REGION || process.env?.FUNCTIONS_REGION)) || firebase.config?.functionsRegion || "europe-west1";
  const cfUrl = functionsUrl
    ? `${functionsUrl}/payBookingWithWallet`
    : `https://${region}-${config?.projectId || firebase.config?.projectId || "drive2go-c806b"}.cloudfunctions.net/payBookingWithWallet`;
  try {
    const { getValidAuthToken } = require("./authactions");
    const token = await getValidAuthToken(true);
    const response = await fetch(cfUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "Authorization": `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ bookingId })
    });
    const json = await response.json().catch(() => null);
    if (response.ok && json?.success) {
      if (json.booking) {
        dispatch({ type: UPDATE_BOOKING, payload: json.booking });
      }
      return json;
    }
    const errMsg = json?.message || json?.error || "Wallet payment failed";
    throw new Error(errMsg);
  } catch (e) {
    console.error("[payBookingWithWallet] error", e);
    throw e;
  }
};

export const switchGuestToOnboard = (bookingId, token) => async (dispatch) => {
  const { config } = firebase;
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
  const functionsUrl = (process && process.env && process.env.REACT_APP_FUNCTIONS_URL)
    ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
    : null;
  let host = origin || functionsUrl || `https://${config?.projectId}.web.app`;
  let url = `${host}/switchGuestToOnboard`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bookingId, token })
    });
    const json = await response.json();
    return json;
  } catch (e) {
    console.error("[switchGuestToOnboard] error", e);
    return { success: false, error: e.message };
  }
};

export const requestGuestCancelOtp = (bookingId, token, locale) => async (dispatch) => {
  const { config } = firebase;
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
  const functionsUrl = (process && process.env && process.env.REACT_APP_FUNCTIONS_URL)
    ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
    : null;
  let host = origin || functionsUrl || `https://${config?.projectId}.web.app`;
  let url = `${host}/requestGuestCancelOtp`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bookingId, token, locale })
    });
    const json = await response.json();
    return json;
  } catch (e) {
    console.error("[requestGuestCancelOtp] error", e);
    return { success: false, error: e.message };
  }
};

export const cancelGuestBooking = (bookingId, token, reason, otp) => async (dispatch) => {
  const { config } = firebase;
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
  const functionsUrl = (process && process.env && process.env.REACT_APP_FUNCTIONS_URL)
    ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
    : null;
  let host = origin || functionsUrl || `https://${config?.projectId}.web.app`;
  let url = `${host}/cancelGuestBooking`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bookingId, token, reason, otp })
    });
    const json = await response.json();
    return json;
  } catch (e) {
    console.error("[cancelGuestBooking] error", e);
    return { success: false, error: e.message };
  }
};